import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { Article, Audience } from "../help/parse";
import { loadHelp } from "../help/load";
import type { HelpScreen } from "../help/screens";
import { navigate } from "../router";
import { openArticle } from "./HelpArticle";

/**
 * The ? on a screen: the help for what is in front of you.
 *
 * `screens` in order of preference: the first one any article names wins, so Plan all stages
 * can ask for its own article and fall back to the task's. One article opens straight away;
 * several open a short list here, so nobody is sent to read the wrong one; none (an article
 * not written yet) opens the whole help rather than doing nothing.
 *
 * The list is PORTALLED to the body and placed against the button, because the ? sits inside
 * headings and pop-ups: inside, it would take a 36px heading's font, and a dialog's scrolling
 * box would clip it.
 */
const AUDIENCE_LABEL: Record<Audience, string> = {
  everyone: "Everyone",
  pm: "PMs and task managers",
  admin: "Admins and HR",
};

export function HelpButton({ screens, label }: { screens: HelpScreen | readonly HelpScreen[]; label?: string }) {
  const wanted: readonly HelpScreen[] = typeof screens === "string" ? [screens] : screens;
  const key = wanted.join(",");
  const [list, setList] = useState<Article[] | null>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Starts loading the help as soon as the ? is on screen, so a press is instant.
  useEffect(() => {
    void loadHelp().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!list) return;
    const close = (e: Event) => {
      const t = e.target as Node | null;
      if (e.type === "mousedown" && t && (popRef.current?.contains(t) || buttonRef.current?.contains(t))) return;
      setList(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Only the list: a ? inside a dialog must not close the dialog as well.
        e.stopImmediatePropagation();
        setList(null);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [list]);

  useEffect(() => {
    if (list) popRef.current?.querySelector("a")?.focus();
  }, [list]);

  const press = async (): Promise<void> => {
    if (list) {
      setList(null);
      return;
    }
    let found: Article[] = [];
    try {
      const { library } = await loadHelp();
      for (const s of wanted) {
        found = library.forScreen(s);
        if (found.length > 0) break;
      }
    } catch {
      // The help chunk did not load; the Help screen says so itself.
    }
    if (found.length === 1 && found[0]) {
      navigate({ kind: "help", id: found[0].id, screen: null });
      return;
    }
    if (found.length === 0) {
      navigate({ kind: "help", id: null, screen: null });
      return;
    }
    const r = buttonRef.current?.getBoundingClientRect();
    const width = 340;
    setAt(
      r
        ? { top: r.bottom + 6, left: Math.max(12, Math.min(r.left - 8, window.innerWidth - width - 12)) }
        : { top: 80, left: 12 },
    );
    setList(found);
  };

  const mixed = list !== null && new Set(list.map((a) => a.audience)).size > 1;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="help-q"
        aria-label={label ?? "Help for this screen"}
        title={label ?? "Help for this screen"}
        aria-haspopup="true"
        aria-expanded={list !== null}
        data-help={key}
        onClick={() => void press()}
      >
        ?
      </button>
      {list && at
        ? createPortal(
            <div
              ref={popRef}
              className="help-pop"
              role="menu"
              aria-label="Help for this screen"
              style={{ top: at.top, left: at.left }}
            >
              <div className="help-pop-head">Help for this screen</div>
              {/* Grouped by who it is for, everyone first, once the list mixes them: the task
                  page alone has articles for everyone, for PMs and for admins. */}
              {list.map((a, i) => (
                <Fragment key={a.id}>
                  {mixed && (i === 0 || list[i - 1]?.audience !== a.audience) ? (
                    <div className={i === 0 ? "help-pop-group first" : "help-pop-group"}>
                      {AUDIENCE_LABEL[a.audience]}
                    </div>
                  ) : null}
                  <a
                    role="menuitem"
                    href={`/help/${a.id}`}
                    className="help-pop-item"
                    onClick={(e) => {
                      setList(null);
                      openArticle(e, a.id);
                    }}
                  >
                    <span className="help-side-id">{a.id}</span>
                    <span>{a.title}</span>
                  </a>
                </Fragment>
              ))}
              <a
                role="menuitem"
                href="/help"
                className="help-pop-all"
                onClick={(e) => {
                  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  setList(null);
                  navigate({ kind: "help", id: null, screen: null });
                }}
              >
                All help →
              </a>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
